import { useEffect } from "react";
import { clearPersistedQueryCache } from "@/lib/query-persist";
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export type CurrentProfile = {
  id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  city: string | null;
  country: string | null;
  favorite_genres: string[];
};

const AUTH_KEY = ["auth-user"] as const;

/**
 * Perf: this hook is mounted by dozens of components at once. Before, each
 * instance fired its own `supabase.auth.getUser()` network request and its own
 * auth subscription, which flooded the app with duplicate requests and made
 * pages load "in pieces". Now the session lives in a single React Query entry
 * (read from local storage via `getSession`, no network round-trip) and a
 * single global auth listener keeps it fresh.
 */
let listenerAttached = false;

function attachGlobalAuthListener(qc: QueryClient) {
  if (listenerAttached || typeof window === "undefined") return;
  listenerAttached = true;
  supabase.auth.onAuthStateChange((_evt, session) => {
    qc.setQueryData(AUTH_KEY, session?.user ?? null);
    qc.invalidateQueries({ queryKey: ["current-profile"] });
  });
}

export function useCurrentUser() {
  const qc = useQueryClient();

  useEffect(() => {
    attachGlobalAuthListener(qc);
  }, [qc]);

  const userQ = useQuery<User | null>({
    queryKey: AUTH_KEY,
    // getSession() reads the persisted session locally — no HTTP request.
    queryFn: async () => {
      const { data } = await supabase.auth.getSession();
      return data.session?.user ?? null;
    },
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });

  const user = userQ.data ?? null;

  const profileQ = useQuery({
    queryKey: ["current-profile", user?.id ?? null],
    enabled: !!user,
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    queryFn: async () => {
      const [{ data, error }, { data: bio }] = await Promise.all([
        supabase
          .from("profiles")
          .select("id, username, display_name, avatar_url, city, country, favorite_genres")
          .eq("id", user!.id)
          .maybeSingle(),
        supabase.rpc("get_profile_bio", { _id: user!.id }),
      ]);
      if (error) throw error;
      return (data ? { ...data, bio: (bio as string | null) ?? null } : null) as CurrentProfile | null;
    },
  });

  return {
    user,
    profile: profileQ.data ?? null,
    loading: userQ.isLoading || (!!user && profileQ.isLoading),
    isAuthenticated: !!user,
  };
}

export async function signOut() {
  clearPersistedQueryCache();
  await supabase.auth.signOut();
}
